import { memo, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tag } from '@carbon/react';
import { Idea } from '@carbon/icons-react';
import type { ExplorationGraphNode } from '@snxt/snxt-onto-share';

export interface ExplorerConceptNodeData
  extends ExplorationGraphNode,
    Record<string, unknown> {}

/**
 * Custom @xyflow/react node for the Knowledge Explorer canvas.
 *
 * Visually aligned with the ontology editor's `ConceptNode`:
 *  - 4-sided floating handles (all sides) for compatibility with FloatingEdge
 *  - Blue circle icon with concept label
 *  - Gray border  → no groundings yet
 *  - Blue border  → grounded (matched to KB knowledge units)
 *  - KU badge     → number of matching knowledge units
 *  - KO badge     → number of distinct Knowledge Objects involved
 *  - Score pill   → average cosine similarity %
 *
 * Read-only mode: no action buttons (expand, delete, lock) — explorer is view-only.
 */
function ExplorerConceptNodeInner({ data, selected }: NodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const d = data as ExplorerConceptNodeData;
  const grounded = (d.grounding_count ?? 0) > 0;
  const pct =
    d.avg_similarity != null ? Math.round(d.avg_similarity * 100) : null;

  const borderColor = selected
    ? 'var(--cds-focus)'
    : grounded
      ? 'var(--cds-interactive-01)'
      : 'var(--cds-border-subtle-01)';

  const boxShadow = selected
    ? '0 0 0 2px var(--cds-focus)'
    : grounded
      ? '0 2px 8px rgba(15,98,254,0.20)'
      : isHovered
        ? '0 2px 8px rgba(0,0,0,0.15)'
        : '0 2px 6px rgba(0,0,0,0.10)';

  return (
    <>
      {/* 4-sided handles — required by FloatingEdge which picks the closest border */}
      <Handle
        id="target-top"
        type="target"
        position={Position.Top}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="target-bottom"
        type="target"
        position={Position.Bottom}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="target-left"
        type="target"
        position={Position.Left}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="target-right"
        type="target"
        position={Position.Right}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="source-top"
        type="source"
        position={Position.Top}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="source-bottom"
        type="source"
        position={Position.Bottom}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="source-left"
        type="source"
        position={Position.Left}
        className="explorer-concept-node__handle"
      />
      <Handle
        id="source-right"
        type="source"
        position={Position.Right}
        className="explorer-concept-node__handle"
      />

      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          minWidth: '180px',
          maxWidth: '280px',
          padding: '0.875rem',
          backgroundColor: 'var(--cds-layer-01)',
          border: `${selected ? 2 : 1}px solid ${borderColor}`,
          borderRadius: '4px',
          boxShadow,
          transition: 'all 0.15s ease',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
          {/* Icon circle */}
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: grounded
                ? 'var(--cds-interactive-01)'
                : 'var(--cds-layer-03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background-color 0.15s ease',
            }}
          >
            <Idea size={14} style={{ fill: grounded ? 'white' : 'var(--cds-icon-secondary)' }} />
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--cds-text-primary)',
                lineHeight: 1.3,
                marginBottom: grounded ? '0.5rem' : 0,
                wordBreak: 'break-word',
              }}
            >
              {d.label}
            </div>

            {/* Grounding badges */}
            {grounded && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.25rem',
                  alignItems: 'center',
                }}
              >
                <Tag size="sm" type="blue">
                  {d.grounding_count} KU{d.grounding_count !== 1 ? 's' : ''}
                </Tag>
                {(d.ko_count ?? 0) > 1 && (
                  <Tag size="sm" type="teal">
                    {d.ko_count} KOs
                  </Tag>
                )}
                {pct != null && (
                  <Tag
                    size="sm"
                    type={pct >= 75 ? 'green' : pct >= 50 ? 'warm-gray' : 'gray'}
                  >
                    {pct}%
                  </Tag>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export const ExplorerConceptNode = memo(ExplorerConceptNodeInner);

// Made with Bob
